import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, User as UserIcon, Pencil, Trash2 } from "lucide-react";
import { ROLE_LABELS } from "@/lib/constants";
import { toast } from "sonner";

const ROLE_BADGE = {
  super_admin: "bg-purple-500/10 text-purple-400 border-purple-500/30",
  assessor: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  client_viewer: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
};

export default function Users() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, item: null });

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
      setItems(u.data); setClients(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-uppercase">Access Control</div>
          <h1 className="text-3xl tracking-tight font-medium mt-2 text-white">Users</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage user accounts and roles.</p>
        </div>
        <Button onClick={() => setDialog({ open: true, item: null })} data-testid="new-user-button" className="bg-white text-black hover:bg-zinc-200">
          <Plus className="w-4 h-4 mr-1.5" /> New User
        </Button>
      </div>

      <div className="mt-6 rounded-md border border-zinc-800 bg-[#121212] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A]">
              <tr>
                <th className="text-left px-4 py-3 label-uppercase">Name</th>
                <th className="text-left px-4 py-3 label-uppercase">Email</th>
                <th className="text-left px-4 py-3 label-uppercase">Role</th>
                <th className="text-left px-4 py-3 label-uppercase">Status</th>
                <th className="text-left px-4 py-3 label-uppercase">Assigned Clients</th>
                <th className="text-right px-4 py-3 label-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t border-zinc-800 hover:bg-zinc-900/40" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 text-white">
                    <span className="inline-flex items-center gap-1.5"><UserIcon className="w-3.5 h-3.5 text-zinc-500" /> {u.name}</span>
                    {u.is_demo && <Badge className="ml-2 bg-orange-500/10 text-orange-400 border-orange-500/30 text-[9px]">DEMO</Badge>}
                  </td>
                  <td className="px-4 py-3 text-zinc-400 mono text-xs">{u.email}</td>
                  <td className="px-4 py-3"><Badge className={`${ROLE_BADGE[u.role]} text-[10px]`}>{ROLE_LABELS[u.role]}</Badge></td>
                  <td className="px-4 py-3"><Badge className={u.is_active ? "bg-green-500/10 text-green-400 border-green-500/30" : "bg-zinc-500/10 text-zinc-500 border-zinc-500/30"}>{u.is_active ? "Active" : "Disabled"}</Badge></td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">
                    {u.role === "client_viewer"
                      ? (u.assigned_client_ids?.length
                        ? clients.filter((c) => u.assigned_client_ids.includes(c.id)).map((c) => c.name).join(", ")
                        : <span className="text-zinc-600">— none —</span>)
                      : <span className="text-zinc-600">all</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => setDialog({ open: true, item: u })} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-white" data-testid={`edit-user-${u.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(u.id)} className="p-1.5 rounded hover:bg-red-500/10 text-zinc-400 hover:text-red-400" data-testid={`del-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <UserDialog open={dialog.open} item={dialog.item} clients={clients} onClose={() => setDialog({ open: false, item: null })} onSaved={load} />
    </div>
  );
}

function UserDialog({ open, item, clients, onClose, onSaved }) {
  const editing = !!item;
  const [form, setForm] = useState({ name: "", email: "", role: "assessor", password: "", assigned_client_ids: [], is_active: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) {
      if (item) setForm({ name: item.name, email: item.email, role: item.role, password: "", assigned_client_ids: item.assigned_client_ids || [], is_active: item.is_active });
      else setForm({ name: "", email: "", role: "assessor", password: "", assigned_client_ids: [], is_active: true });
    }
  }, [open, item]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        const { name, role, is_active, assigned_client_ids } = form;
        await api.patch(`/users/${item.id}`, { name, role, is_active, assigned_client_ids });
      } else {
        await api.post("/users", form);
      }
      toast.success(editing ? "Updated" : "Created");
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const toggleClient = (id) => {
    setForm((f) => ({
      ...f,
      assigned_client_ids: f.assigned_client_ids.includes(id)
        ? f.assigned_client_ids.filter((x) => x !== id)
        : [...f.assigned_client_ids, id],
    }));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white sm:max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="label-uppercase">Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-uppercase">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required disabled={editing} data-testid="user-email-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800" />
            </div>
            <div>
              <Label className="label-uppercase">Role</Label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select"
                className="mt-1.5 w-full h-10 px-3 rounded-md bg-[#0A0A0A] border border-zinc-800 text-sm">
                <option value="super_admin">Super Admin</option>
                <option value="assessor">Assessor</option>
                <option value="client_viewer">Client Viewer</option>
              </select>
            </div>
          </div>
          {!editing && (
            <div>
              <Label className="label-uppercase">Initial Password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} data-testid="user-password-input" className="mt-1.5 bg-[#0A0A0A] border-zinc-800 mono" />
              <div className="text-[10px] text-zinc-500 mt-1">User will be required to change password on first login.</div>
            </div>
          )}
          {editing && (
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="user-active-switch" />
              <span className="text-xs text-zinc-400">{form.is_active ? "Active" : "Disabled"}</span>
            </div>
          )}
          {form.role === "client_viewer" && (
            <div>
              <Label className="label-uppercase">Assigned Clients</Label>
              <div className="mt-1.5 max-h-40 overflow-y-auto border border-zinc-800 rounded-md p-2 space-y-1">
                {clients.length === 0 && <div className="text-xs text-zinc-500">No clients available.</div>}
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-zinc-900 cursor-pointer">
                    <input type="checkbox" checked={form.assigned_client_ids.includes(c.id)} onChange={() => toggleClient(c.id)} className="accent-white" data-testid={`assign-client-${c.id}`} />
                    <span className="text-xs text-zinc-300">{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 pt-3">
            <Button type="button" variant="outline" onClick={onClose} className="border-zinc-800 text-zinc-300 hover:bg-zinc-900">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-white text-black hover:bg-zinc-200" data-testid="user-submit-button">
              {busy ? "Saving…" : editing ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
